<template>
  <div
    class="absolute"
    :style="{
      width: table.width + 'px',
      height: table.height + 'px',
      left: table.x + 'px',
      top: table.y + 'px'
    }"
  >
    <l-dropdown
      v-model:open="contextMenu"
      :options="options"
      :mark-selected="false"
      placement="top-start"
      @update:model-value="executeAction"
    >
      <div
        :style="[
          animationStyle,
          {
            width: props.table.width + 'px',
            height: props.table.height + 'px',
            fontSize: fontSize / 16 + 'rem'
          }
        ]"
        class="relative flex items-center justify-center cursor-pointer shadow-[0_0_10px_0_#22213433]"
        :class="{
          'opacity-30': isBlocked,
          'rounded-[0.5em]': table.type === 'rectangle',
          'rounded-[200px]': table.type === 'circle',
          'bg-n-600 text-n-300': table.type === 'wall',

          'bg-n-0 text-n-900': tableState === 'empty' && !hasNextReservation,
          'bg-[#3399FF] text-n-0': tableState === 'occupied',
          'bg-[#FF4D4D] text-n-0': tableState === 'toPay',
          'bg-[#FFD442] text-n-900': tableState === 'reserved' || hasNextReservation,

          'shadow-[inset_0_0_0_0.25em_#FFD442]': hasNearReservation,
          '!bg-v-300 moving !text-n-0': isSelected && !isBlocked
        }"
        v-on-long-press.prevent="showContextMenu"
        @contextmenu.prevent="showContextMenu()"
        @click="!isBlocked && $emit('selectedTable')"
      >
        <div class="w-full h-full px-[0.5em] py-[0.75em]">
          <div
            class="flex flex-col w-full h-full gap-[0.25em]"
            :class="{
              'justify-center': table.type === 'circle' || table.type === 'wall',
              'justify-between': table.type === 'rectangle'
            }"
          >
            <div
              class="flex items-center justify-between relative"
              :class="{
                'justify-center': table.type === 'circle' || table.type === 'wall'
              }"
            >
              <div
                class="uppercase font-heading truncate leading-none text-[1.25em] min-w-0 flex-1 break-all"
                :class="{
                  'text-center': table.type === 'circle' || table.type === 'wall',
                  'text-n-200': table.type === 'wall',
                  'font-bold': table.type !== 'wall'
                }"
              >
                <span>{{ name }}</span>
                <span v-if="groupedTableNames.length > 0" class="font-normal">
                  {{ ', ' + groupedTableNames.join(', ') }}
                </span>
              </div>
              <div
                v-if="
                  table.type !== 'circle' &&
                  ((showActualStatus && pendingKitchenOrders) ||
                    (showActualStatus && table.tabIds.length > 1))
                "
                class="relative w-[1em] h-full"
              >
                <div class="absolute -top-1 right-0 flex flex-col gap-[0.25em]">
                  <div
                    v-if="showActualStatus && pendingKitchenOrders"
                    class="bg-n-0 rounded-full w-[1em] h-[1em] relative"
                  >
                    <div class="absolute inset-[1px] rounded-full bg-[#FFD442]" />
                  </div>

                  <div
                    v-if="showActualStatus && table.tabIds.length > 1"
                    class="bg-n-0 rounded-full w-[1em] h-[1em] relative"
                  >
                    <div
                      class="absolute inset-[1px] rounded-full bg-[#9C6DFE] text-[0.625em] leading-none text-center font-medium flex flex-col justify-center items-center"
                    >
                      {{ table.tabIds.length }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="table.type !== 'wall'"
              class="leading-none flex flex-col gap-[0.25em] text-[0.75em] font-medium"
              :class="{
                'items-center': table.type === 'circle'
              }"
            >
              <div class="opacity-60" v-if="showActualStatus || mode === 'seats'">
                <template v-if="mode === 'seats'">
                  {{
                    t('floorplan.n-seats', {
                      seats: table.max
                    })
                  }}
                </template>
                <template v-else>
                  <span v-if="hasNextReservation || tableState === 'reserved'">
                    {{ t('floorplan.reserved') }}
                  </span>
                  <span v-else-if="tableState === 'empty'">
                    {{ t('floorplan.free') }}
                  </span>
                  <span v-else-if="tableState === 'toPay'">
                    {{ t('floorplan.to-pay') }}
                  </span>
                  <span v-else>{{ t('floorplan.occupied') }}</span>
                </template>
              </div>

              <div>
                <div
                  v-if="(hasNearReservation && reservation) || hasNextReservation"
                  class="truncate text-[1.12em] font-bold capitalize"
                  :class="{
                    'text-center': table.type === 'circle'
                  }"
                >
                  {{ reservation?.name ?? nextReservation?.name }}
                </div>

                <template v-if="mode === 'time'">
                  <template v-if="showActualStatus && table.tabIds.length > 0">
                    {{ elapsedTime }}
                  </template>
                  <template v-else-if="showActualStatus && hasNearReservation">
                    <div
                      class="flex items-center gap-[0.25em]"
                      :class="{
                        'text-[#FF4D4D]': reservationTime?.isLate,
                        'flex-col-reverse gap-0': table.type === 'circle'
                      }"
                    >
                      <div
                        v-if="reservationTime?.isLate"
                        class="bg-[#FF4D4D] h-[0.5em] w-[0.5em] rounded-full"
                      ></div>
                      {{ reservationTime?.time }}
                    </div>
                  </template>
                </template>

                <template v-else-if="mode === 'diners' && seats > 0">
                  {{ seats }} {{ t('floorplan.diners') }}
                </template>

                <template v-else-if="mode === 'amount' && total > 0">
                  {{ currency(total) }}
                </template>

                <template v-else-if="mode === 'reservation' && nextReservation">
                  <div
                    :class="{
                      'text-center': table.type === 'circle'
                    }"
                  >
                    {{ format(nextReservation.dateTime, 'HH:mm') }} ·
                    {{
                      t('floorplan.number-diners', {
                        diners: nextReservation.diners
                      })
                    }}
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <template v-if="table.type === 'circle'">
          <div
            v-if="showActualStatus && pendingKitchenOrders"
            class="absolute bg-n-0 rounded-full w-[1em] h-[1em] translate-x-1/2 translate-y-1/2"
            :style="bottomRightStyles"
          >
            <div class="absolute inset-[1px] rounded-full bg-[#FFD442]" />
          </div>

          <div
            v-if="showActualStatus && table.tabIds.length > 1"
            class="absolute bg-n-0 rounded-full w-[1em] h-[1em] -translate-x-1/2 translate-y-1/2"
            :style="bottomLeftStyles"
          >
            <div
              class="absolute inset-[1px] rounded-full bg-[#9C6DFE] text-[0.625em] leading-none text-center font-medium flex flex-col justify-center items-center"
            >
              {{ table.tabIds.length }}
            </div>
          </div>
        </template>
      </div>
    </l-dropdown>
  </div>
</template>

<script setup lang="ts">
import { vOnLongPress } from '@vueuse/components'
import { differenceInMinutes, format } from 'date-fns'
import { storeToRefs } from 'pinia'
import type { StyleValue } from 'vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LDropdown } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/dropdown/types'

import { useMoney } from '@/composables/useMoney'
import { useTabs } from '@/composables/useTabs'
import { useConfigStore } from '@/store/config'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { Tab, Table, TableWithTabs } from '@/types'

import { mapTableStateToMenuItems, type TableStates } from './table.utils'

type Props = {
  table: TableWithTabs
  mode: 'time' | 'amount' | 'diners' | 'reservation' | 'seats'
  groupedTableNames: string[]
  isSelected?: boolean
  isBlocked?: boolean
  showActualStatus?: boolean
  contextMenuEnabled?: boolean
  time: Date
}

const props = withDefaults(defineProps<Props>(), {
  isSelected: false,
  isBlocked: false,
  showActualStatus: true,
  contextMenuEnabled: true
})

const emit = defineEmits<{
  selectedTable: []
  mergeTabs: [string]
  assignTable: [string[]]
  swapTables: [string]
}>()

const router = useRouter()
const { currency } = useMoney()
const { t } = useI18n()

const tabsStore = useTabsStore()
const configStore = useConfigStore()
const reservationsStore = useReservationsStore()

const { searchReservation, nextTableReservation } = storeToRefs(reservationsStore)
const { tabs, kitchenOrders, getLastInteraction } = storeToRefs(tabsStore)
const { config } = storeToRefs(configStore)

const contextMenu = ref(false)

const options = computed<Option[]>(() => {
  return mapTableStateToMenuItems[tableState.value]
})

const tab = computed(() => {
  return tabs.value[props.table.tabIds[0]]
})

const tableState = computed<TableStates>(() => {
  const tableHasTab = hasTab.value
  const tableHasReservation = hasNearReservation.value
  const tableHasBills = hasBills.value

  if ((!tableHasTab && !tableHasReservation && !tableHasBills) || !props.showActualStatus) {
    return 'empty'
  } else if (tableHasBills) {
    return 'toPay'
  } else if (tableHasTab) {
    return 'occupied'
  } else if (tableHasReservation) {
    return 'reserved'
  } else {
    return 'empty'
  }
})

const fontSize = computed(() => {
  if (Math.min(props.table.height, props.table.width) < 90) {
    return 16 * (Math.min(props.table.height, props.table.width) / 90)
  }
  return 16
})

const name = computed(() => {
  if (tab.value && tab.value.lang && tab.value.lang !== configStore.config.locationCountryCode) {
    return `${tab.value.lang} - ${props.table.name}`
  }
  return props.table.name
})

const seats = computed(() => {
  if (tab.value) {
    return tab.value.seats?.length || 0
  }
  const reservation = searchReservation.value(props.table.id, props.time)
  if (reservation && props.showActualStatus) {
    return reservation.diners || 0
  }
  return 0
})

const total = computed(() => {
  return props.table.tabIds.reduce((total, id) => {
    const { total: tabTotal } = useTabs(id)
    return total + (tabTotal.value ?? 0)
  }, 0)
})

const pendingKitchenOrders = computed(() => {
  if (!config.value.enableKitchenOrders) return false
  return props.table.tabIds.some(tabId => {
    const { unsentProducts } = useTabs(tabId)
    return (
      unsentProducts.value.length > 0 ||
      tabs.value[tabId].kitchenOrders
        .map((id: string) => kitchenOrders.value[id])
        .filter(order => !order.printedTime && order.copies > 0).length > 0
    )
  })
})

const hasTab = computed(() => {
  return props.table.tabIds.length > 0
})

const hasBills = computed(() => {
  if (props.table.tabIds.length === 0) return false
  return props.table.tabIds.some(tabId => {
    return (tabs.value[tabId] as Tab)?.billingStartedTime
  })
})

const nextReservation = computed(() => {
  return nextTableReservation.value(props.table.id, props.time)
})

const reservation = computed(() => {
  return searchReservation.value(props.table.id, props.time)
})

const hasNextReservation = computed(() => {
  return props.mode === 'reservation' && !!nextReservation.value
})

const hasNearReservation = computed(() => {
  return props.showActualStatus && !!reservation.value && reservation.value.status !== 'cancelled'
})

const reservationTime = computed(() => {
  if (!reservation.value) return
  const date = reservation.value.dateTime
  if (!date) return
  const now = props.time
  const duration = differenceInMinutes(date, now)

  const hours = Math.floor(Math.abs(duration) / 60)
  const minutes = Math.abs(duration) % 60
  const formattedMinutes = String(minutes).padStart(2, '0')

  return {
    isLate: duration < 0,
    time:
      duration > 0
        ? hours > 0
          ? t('floorplan.in-time-hours', {
              hours,
              minutes: formattedMinutes
            })
          : t('floorplan.in-time-minutes', {
              minutes
            })
        : t('floorplan.late', {
            minutes
          })
  }
})

const elapsedTime = computed(() => {
  const date = getLastInteraction.value(props.table.tabIds[0])
  if (!date) return

  const now = props.time
  const minutes = Math.floor(Math.max(differenceInMinutes(now, date), 0))
  const roundMinutes = Array.from(`${minutes % 60}`).length <= 1 ? `0${minutes % 60}` : minutes % 60

  return `${Math.floor(minutes / 60)}:${roundMinutes}`
})

const bottomRightStyles = computed((): Partial<StyleValue> => {
  return {
    bottom: getCircleInset(props.table) + 'px',
    right: getCircleInset(props.table) + 'px'
  }
})

const bottomLeftStyles = computed((): Partial<StyleValue> => {
  return {
    bottom: getCircleInset(props.table) + 'px',
    left: getCircleInset(props.table) + 'px'
  }
})

function getCircleInset(table: Table): number {
  const hipotenuse = Math.min(table.width, table.height) / 2
  const cathetus = Math.sqrt((hipotenuse * hipotenuse) / 2)

  return hipotenuse - cathetus
}

function showContextMenu() {
  if (!props.contextMenuEnabled || tableState.value === 'empty' || !props.showActualStatus) return
  contextMenu.value = true
}

function hideContextMenu() {
  contextMenu.value = false
}

const animationStyle = computed((): Partial<StyleValue> => {
  if (props.table.type === 'wall') return ''
  return {
    animationDuration: 0.3 + 's',
    animationDelay: -(Math.random() + 0.3) + 's',
    ['--table-animation-variant' as string]:
      getAngle(Math.max(props.table.width, props.table.height)) + 'deg'
  }
})

function getAngle(size: number): number {
  const maxAngle = 6
  const maxSize = 200
  const stepSize = maxSize / maxAngle

  const angle = maxAngle - Math.round(size / stepSize)

  return angle <= 2 ? 2 : angle >= maxAngle ? maxAngle : angle
}

function executeAction(action: (string | number)[]) {
  const tabs = useTabs(props.table.tabIds[0])
  switch (action[0]) {
    case 'printTicket':
      tabs.printBill()
      break
    case 'moveAccount':
      emit('assignTable', props.table.tabIds)
      break
    case 'mergeTabs':
      emit('mergeTabs', props.table.tabIds[0])
      break
    case 'swapTables':
      emit('swapTables', props.table.id)
      break
    case 'closeTable':
      tabs.closeTab()
      break
    case 'moveReservation':
      if (!hasNearReservation.value) {
        break
      }
      router.push({
        name: 'reservations',
        query: { editReservation: reservation.value!.id, showSelector: 1 }
      })
      break
  }
  hideContextMenu()
}
</script>

<style scoped>
:root {
  --table-animation-variant: 1deg;
}

.moving {
  animation-name: wiggle;
  animation-iteration-count: infinite;
}

@keyframes wiggle {
  0% {
    transform: rotate(var(--table-animation-variant));
    animation-timing-function: ease-in;
  }
  50% {
    transform: rotate(calc(var(--table-animation-variant) * -1));
    animation-timing-function: ease-out;
  }
  100% {
    transform: rotate(var(--table-animation-variant));
    animation-timing-function: ease-out;
  }
}
</style>
